/** @format */
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	HostListener,
	inject,
	input,
	OnInit,
	signal,
	untracked
} from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAddToCartButtonTextSDDynamicEnabled } from './+state/add-to-cart-button.selector';

import { UtilityService, WINDOW } from '@shared/data-access/services';
import { DOCUMENT } from '@angular/common';
import {
	AddToCartButtonService,
	addToCartTriggered,
	ATC_BUTTON_TEXTS,
	atcAttemptedWithAttributeErrors,
	quantityRangeValidationFailed,
	quantityRangeValidationSucceeded,
	selectAtcUserInputQty,
	selectInventoryExceededStatus,
	userSetInputQuantity
} from '@pdp/add-to-cart/data-access';
import {
	selectAllProductSkusPartNumber,
	selectIsComingSoon,
	selectIsPersonalizedProduct,
	selectKillswitch,
	selectResolvedSku,
	selectResolvedSkuPartNumber,
	selectSelectedPersonalizationOptions
} from '@pdp/data-access/state';
import { AttributesUtilityService } from '@pdp/attributes/data-access';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { MyCustomizations } from '@shared/data-access/interfaces';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { SeeAvailableStoresButtonComponent } from '../see-available-stores-button/see-available-stores-button.component';

@Component({
	selector: 'pdp-feature-add-to-cart-button',
	templateUrl: './add-to-cart-button.component.html',
	styleUrl: './add-to-cart-button.component.scss',
	imports: [
		ReactiveFormsModule,
		FormsModule,
		HmfButtonComponent,
		SeeAvailableStoresButtonComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddToCartButtonComponent implements OnInit {
	private readonly store = inject(Store);
	private readonly attributesUtilityService = inject(AttributesUtilityService);
	private readonly addToCartButtonService = inject(AddToCartButtonService);
	private readonly utilityService = inject(UtilityService);
	private readonly window = inject(WINDOW) as Window;
	private readonly document = inject(DOCUMENT);

	constructor() {
		effect(() => {
			this.inventoryExceededStatusFromState(); // just here to trigger effect
			const value = this.atcUserInputQtyFromState();
			untracked(() => {
				if (value !== this.userInputQuantity()) {
					this.validateQuantity(value, true);
				}
			});
		});

		effect(() => {
			this.resolvedSku(); // just here to trigger effect
			untracked(() => {
				if (this.minQty() > 1 && this.userInputQuantity() < this.minQty()) {
					this.store.dispatch(
						quantityRangeValidationFailed({
							message: `${DCSG_CONSTANTS.minQtyAlert.message} ${this.minQty()}`
						})
					);
					this.userInputQuantity.set(this.minQty());
					this.setUserInputQuantityInState(this.userInputQuantity());
				}
			});
		});

		effect(() => {
			if (this.resolvedSkuPartNumber()) {
				this.isErrorOnAttributes.set(false);
			}
		});

		effect(() => {
			if (this.selectedPersonalizationOptions()?.valid) {
				this.isErrorOnAttributes.set(false);
			}
		});
	}

	readonly ecode = input('');

	readonly badge = input('');

	readonly isCustomizable = input(false);

	readonly customizedAttributes = input<MyCustomizations>(null);

	readonly showQuantityInput = input(true);

	readonly disabled = input(false);

	allProductSkuPartNumbers = this.store.selectSignal(selectAllProductSkusPartNumber);
	resolvedSkuPartNumber = this.store.selectSignal(selectResolvedSkuPartNumber);
	selectedPersonalizationOptions = this.store.selectSignal(selectSelectedPersonalizationOptions);
	isPersonalizedProduct = this.store.selectSignal(selectIsPersonalizedProduct);
	resolvedSku = this.store.selectSignal(selectResolvedSku);
	inventoryExceededStatus = this.store.selectSignal(selectInventoryExceededStatus);

	minQty = computed(() => {
		const sku = this.resolvedSku();
		return sku?.prices?.minQty ?? 1;
	});
	maxQty = computed(() => {
		const sku = this.resolvedSku();
		return sku?.prices?.maxQty ?? 99;
	});

	readonly nearbyStoresButtonEnabled = this.store.selectSignal(
		selectKillswitch('nearbyStoresButtonEnabled')
	);
	readonly notifyUserWhenAvailableEnabled = this.store.selectSignal(
		selectKillswitch('notifyUserWhenAvailableEnabled')
	);

	readonly isComingSoon = this.store.selectSignal(selectIsComingSoon);
	readonly buttonTextFromState = this.store.selectSignal(
		selectAddToCartButtonTextSDDynamicEnabled
	);

	/**
	 * @description the value entered by user in the quantity input
	 */
	userInputQuantity = signal(1);
	isErrorOnAttributes = signal(false);
	storeSelectionReady = signal(false);

	atcUserInputQtyFromState = this.store.selectSignal(selectAtcUserInputQty);
	inventoryExceededStatusFromState = this.store.selectSignal(selectInventoryExceededStatus);

	readonly buttonText = computed(() => {
		const isComingSoon = this.isComingSoon();
		const buttonText = this.buttonTextFromState();
		const notifyMeEnabled = this.notifyUserWhenAvailableEnabled();
		if (!buttonText) {
			return buttonText;
		}
		if (isComingSoon) {
			return {
				...buttonText,
				addToCartButtonText: 'Coming Soon',
				addToCartButtonDisabled: true
			};
		}
		// Handle notify me killswitch
		if (!notifyMeEnabled && buttonText.addToCartButtonText === ATC_BUTTON_TEXTS.notifyMe) {
			return {
				...buttonText,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: true
			};
		}
		return buttonText;
	});

	atcButtonTexts = ATC_BUTTON_TEXTS;

	ngOnInit(): void {
		if (window.headerFunctions?.storeSelection) {
			this.storeSelectionReady.set(true);
		}
	}

	addToCartButtonClicked(buttonType: string, buttonAction: string) {
		// if button action is SelectOptions scroll user to attributes
		if (buttonAction === 'SelectOptions') {
			this.isErrorOnAttributes.set(
				this.attributesUtilityService.validateIfAttributeErrorsExist(
					'[add-to-cart-button] addToCartButtonClicked',
					this.isPersonalizedProduct()
				)
			);
			// scroll to error
			if (this.isErrorOnAttributes()) {
				this.utilityService.scrollToElement(
					this.document.getElementById('attributes-v2-container'),
					{
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest'
					}
				);
				this.store.dispatch(atcAttemptedWithAttributeErrors());
			}
		} else if (buttonType === ATC_BUTTON_TEXTS.noStoreSelected) {
			const storeSelectionValues = this.resolvedSkuPartNumber()
				? {
						skusArr: this.allProductSkuPartNumbers(),
						selectedSkuVal: this.resolvedSkuPartNumber()
					}
				: { skusArr: this.allProductSkuPartNumbers() };
			if (window.headerFunctions?.storeSelection) {
				this.window.headerFunctions.storeSelection(storeSelectionValues);
			}
		} else if (
			buttonType === ATC_BUTTON_TEXTS.noDeliveryLocation ||
			buttonType === ATC_BUTTON_TEXTS.enterZip
		) {
			this.window.headerFunctions.sameDayDelivery();
		} else if (
			buttonType === ATC_BUTTON_TEXTS.atc ||
			buttonType === ATC_BUTTON_TEXTS.preOrder
		) {
			this.store.dispatch(addToCartTriggered());
			this.addToCartButtonService.emitAddToCartClick();
		} else if (buttonType === ATC_BUTTON_TEXTS.customize) {
			this.addToCartButtonService.emitOpenCustomPanel(true);
		} else if (buttonType === ATC_BUTTON_TEXTS.notifyMe) {
			this.addToCartButtonService.emitOpenNotifyMePanel(true);
		}
	}

	/**
	 * @description Get the value from the Qty input element
	 * convert it to a number and set default value of 1
	 * or take the user entered value
	 * @param userInputQty - this is the key event made on the input element
	 * @param triggeredByState - was this function triggered by a state change (instead of the input element?)
	 */
	validateQuantity(userInputQty, triggeredByState = false) {
		const userInputQtyToNumber = this.utilityService.convertToNumber(userInputQty);
		this.checkMinMax();

		if (
			userInputQtyToNumber === 0 ||
			userInputQtyToNumber === undefined ||
			isNaN(userInputQty) ||
			userInputQty === ''
		) {
			this.userInputQuantity.set(1);
			this.setUserInputQuantityInState(1);
		} else if (userInputQtyToNumber > this.maxQty()) {
			this.userInputQuantity.set(this.maxQty());
			this.setUserInputQuantityInState(this.maxQty());
		} else if (userInputQtyToNumber < this.minQty()) {
			this.userInputQuantity.set(this.minQty());
			this.setUserInputQuantityInState(this.minQty());
		} else if (triggeredByState) {
			// if qty is valid and this was triggered by state, then the state is already updated but input isn't
			this.userInputQuantity.set(userInputQtyToNumber);
		} else {
			this.setUserInputQuantityInState(userInputQty);
		}
	}

	/**
	 * @description dispatch the action to update the user input quantity * in state
	 * @param quantity the value from the input HTML element
	 */
	setUserInputQuantityInState(quantity) {
		this.store.dispatch(
			userSetInputQuantity({
				atcUserInputQuantity: quantity.toString()
			})
		);
	}

	checkMinMax() {
		const qty = this.utilityService.convertToNumber(this.userInputQuantity());

		const quantityAlertMessage = this.getQuantityAlertMessage(
			qty,
			this.minQty(),
			this.maxQty()
		);
		if (quantityAlertMessage) {
			this.store.dispatch(
				quantityRangeValidationFailed({
					message: quantityAlertMessage
				})
			);
		} else {
			this.store.dispatch(quantityRangeValidationSucceeded());
		}
	}

	getQuantityAlertMessage(qty: number, minQty: number, maxQty: number): string | null {
		if (qty < minQty) {
			return `${DCSG_CONSTANTS.minQtyAlert.message} ${minQty}`;
		}
		if (qty > maxQty) {
			return `${DCSG_CONSTANTS.maxQtyAlert.message} ${maxQty}`;
		}
		return null;
	}

	@HostListener('window:HeaderServices:StoreSelection:Ready', ['$event'])
	public onStoreSelectionReady(customEvent: CustomEvent): void {
		if (customEvent) {
			this.storeSelectionReady.set(true);
		}
	}
}
